import isEmpty from 'lodash/isEmpty';
import React, { ReactElement } from 'react';
import { AccountAttributes } from '@loyalty/shared-common/gamification-api-client';
import { QlfBreadcrumbs, QlfMainTopbar } from '@qualifio/ui-commons';
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown';
import { useLocation } from 'react-router-dom';
import { useNavigate } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useTranslation } from 'react-i18next';
import { AccountSwitcher } from '../account-switcher/AccountSwitcher';
import { EditableProgramName } from '../../loyalty';
import { LanguageSwitcher } from '../language-switcher/LanguageSwitcher';
import { Router } from '../../services';
import { SettingsDropdown } from '../account-settings/SettingsDropdown';
import { UserProfileMenu } from '../../auth/components/UserProfileMenu';
import { isInShell } from '../../shell/shell';
import { useAccounts } from '../../accounts/AccountsContext';
import { useLanguages } from '../../languages/LanguagesContext';
import { useProgram } from '../../loyalty/ProgramContext';
import { usePrograms } from '../../loyalty/ProgramsContext';

export interface TopBarProps {
  withLogo?: boolean;
}

export function TopBar({ withLogo = true }: TopBarProps): ReactElement {
  const { setCurrentAccount, currentAccount } = useAccounts();
  const { setCurrentLanguage } = useLanguages();
  const { program, setProgram } = useProgram();
  const { updateProgram } = usePrograms();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs: { label: string | ReactElement; path?: string; icon?: any }[] = [
    {
      icon: faCrown,
      label: t('loyalty.name'),
      path: Router.getPath('loyaltyPrograms', { accountId: currentAccount.id }),
    },
    ...(!isEmpty(program)
      ? [
          {
            label: (
              <EditableProgramName
                program={program}
                onSaved={program => {
                  setProgram(program);
                  updateProgram(program.id, program);
                }}
              />
            ),
          },
        ]
      : []),
    ...(Router.isActive('integrations', location) ? [{ label: t('integrations.breadcrumb') }] : []),

    ...(Router.isActive('pushRules', location) ? [{ label: t('push-rules.breadcrumb') }] : []),
    ...(Router.isActive('customKeys', location) ? [{ label: t('custom-keys.breadcrumb') }] : []),
    ...(Router.isActive('interactions', location) ? [{ label: t('interactions.breadcrumb') }] : []),
    ...(Router.isActive('eventReports', location)
      ? [{ label: t('event-reports.breadcrumb') }]
      : []),
    ...(Router.isActive('domainsManagement', location)
      ? [{ label: t('domains-management.breadcrumb') }]
      : []),
    ...(Router.isActive('testUsers', location) ? [{ label: t('test-users.breadcrumb') }] : []),
    ...(Router.isActive('loginPages', location) ? [{ label: t('login-pages.breadcrumb') }] : []),
  ].filter(l => !!l);

  const handleAccountChange = (account: AccountAttributes) => {
    setCurrentAccount(account);
    navigate(
      Router.getPath('loyaltyPrograms', {
        accountId: account.id,
      }),
    );
  };

  return isInShell() ? (
    <></>
  ) : (
    <QlfMainTopbar
      withLogo={withLogo}
      breadcrumbs={<QlfBreadcrumbs links={breadcrumbs} className="loyalty-main-topbar-links" />}
      actions={
        <>
          <AccountSwitcher onChange={handleAccountChange} />
          <UserProfileMenu />
          <SettingsDropdown />
          <LanguageSwitcher onChange={setCurrentLanguage} />
        </>
      }
    />
  );
}
